/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import { isSupported } from '@twilio/video-processors';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BiMessageAltDetail } from 'react-icons/bi';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LocalTrack } from 'twilio-video';

import Button from '../../Components/Button/Button';
import ProviderProfileCardModal from '../../Dashboard/ProfileCards/Provider/ProfileCards';
import { useSubscribe, useVideoContext, useWindowSize } from '../../hooks';
import { useAppSelector } from '../../hooks/useReduxHook';
import { Subscriber } from '../../hooks/useSubscribe';
import { getActiveVideoRoom } from '../../services/cases/getActiveVideoRoom';
import { getOneCase } from '../../services/cases/getCaseById';
import { joinVideoRoom } from '../../services/cases/joinVideoRoom';
import { CaseType, ICase } from '../../services/cases/types/ICase';
import { getUserAvatar } from '../../services/identity/getUserAvatar';
import { UserRoleName } from '../../services/identity/types/UserProfile';
import { displayErrorDetails } from '../../Utils/displayError';
import rightImage from '../assets/waiting-room.svg';
import ToggleAudioButton from '../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../Buttons/ToggleVideoButton/ToggleVideoButton';
import CameraSwitch from '../Buttons/VideoInputList/VideoInputList';
import VirtualBackgroundButton from '../Buttons/VirtualBackgroundButton/VirtualBackgroundButton';
import LocalVideoPreview from '../LocalVideoPreview/LocalVideoPreview';
import styles from './styles.module.scss';
import { Avatar, Box, Button as MuiButton, Typography, useTheme } from '@mui/material';
import EndCallButton from '@brands/VideoRoom/Buttons/EndCallButton/EndCallButton';
import { getFriendlyName } from '@brands/Utils/getFriendlyName';
import { formatDateTime } from '../../Dashboard/ConsultationPage/utils/consultationHelpers';

interface WaitingRoomProps {
  startVideoRoom: (videoSid: string, localTracks: LocalTrack[], dependentId?: string) => Promise<void>;
  setIsJoined: (value: boolean) => void;
  endCurrentCall: boolean | undefined;
  layout?: 'mini' | 'full';
}

const useContainerSize = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;

    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setSize({ width: rect.width, height: rect.height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, ...size };
};

const WaitingRoom: React.FC<WaitingRoomProps> = ({ startVideoRoom, setIsJoined, endCurrentCall, layout }) => {
  const theme = useTheme();
  const { userInfo } = useAppSelector(selectAuth);
  const { vc_case_id: vcCaseID, is_visible: isVisible } = useAppSelector(selectVideoCallCredentials);
  const p = useParams();
  const screenSize = useWindowSize();
  const [providerProfileCardModal, setProviderProfileCardModal] = useState(false);
  const { getAudioAndVideoTracks, localTracks } = useVideoContext();
  const [nonPatientParticipantIds, setNonPatientParticipantIds] = useState<number[]>([]);
  const [currentCase, setCurrentCase] = useState<ICase>();
  const { subscribe, unsubscribe } = useSubscribe();
  const [waitForTracks, setWaitForTracks] = useState<boolean>(false);
  const [disableBtn, setDisableBtn] = useState<boolean>(false);

  const isMobile = screenSize.width < 768;

  const { ref: containerRef, width: containerWidth, height: containerHeight } = useContainerSize();
  const isMini = containerWidth > 0 && containerWidth < 650;

  const checkPatientStatus = useCallback(
    (type = 'join'): boolean => {
      if (userInfo?.role?.name === UserRoleName.Patient) {
        switch (currentCase?.status) {
          case 'new':
            return type !== 'notification';
          case 'accepted_care_advisor':
            return true;
          case 'accepted_provider':
            return true;
          case 'in_progress_provider':
            return true;
          case 'in_progress_care_advisor':
            return true;
          case 'completed':
            return false;
          case 'canceled':
            return false;
          default:
            return false;
        }
      }
      return false;
    },
    [userInfo, currentCase],
  );

  const dispatchSuccessEvent = (event: string, video_room_id: string): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      userId: userInfo.id,
      video_room_id,
    });
  };

  const stopCamera = useCallback(() => {
    localTracks.forEach((track) => {
      if ('stop' in track && typeof track.stop === 'function') {
        track.stop();
      }
    });
  }, [localTracks]);

  useEffect(() => {
    if (!isVisible) {
      stopCamera();
    }
  }, [isVisible]);

  const [isPatientAbleToJoin, setIsPatientAbleToJoin] = useState<boolean>(false);

  useEffect(() => {
    const getRemainingTimeForPatient = (): number => {
      if (
        userInfo?.role?.name === UserRoleName.Patient &&
        (currentCase?.type === CaseType.video_call_scheduled || currentCase?.type === CaseType.oral_health_coaching)
      ) {
        const utcTime = moment().utc().format('YYYY-MM-DDTHH:mm:ss');

        const scheduledTimeUTC = moment(currentCase.video_conference_date).format('YYYY-MM-DDTHH:mm:ss');

        const dueDateObject = new Date(scheduledTimeUTC);
        const currentTime = new Date(utcTime);
        const remainingTime = dueDateObject.getTime() - currentTime.getTime();

        const remainingMinutes = Math.ceil(remainingTime / (1000 * 60));

        return remainingMinutes;
      }
      return 0;
    };

    const updateIsPatientAbleToJoin = (): void => {
      const remainingTime = getRemainingTimeForPatient();
      setIsPatientAbleToJoin(remainingTime <= 10);
    };

    updateIsPatientAbleToJoin();

    const intervalId = setInterval(updateIsPatientAbleToJoin, 60000);

    return () => clearInterval(intervalId);
  }, [currentCase]);

  const subscriber: Subscriber = {
    onMessage: (message: Record<string, any>) => {
      if (
        message.case_id === Number(userInfo?.role?.name === UserRoleName.Patient ? p.caseId : vcCaseID) &&
        message.participant_role !== UserRoleName.Patient
      ) {
        if (message.command === 'participant_connected') {
          setNonPatientParticipantIds([
            ...nonPatientParticipantIds.filter((id) => id !== message.participant_identity),
            message.participant_identity,
          ]);
        } else {
          setNonPatientParticipantIds(nonPatientParticipantIds.filter((id) => id !== message.participant_identity));
        }
      }
    },
    commands: ['participant_connected', 'participant_disconnected'],
  };

  const start = async (): Promise<boolean> => {
    try {
      setDisableBtn(true);

      const targetCaseId = Number(userInfo?.role?.name === UserRoleName.Patient ? p.caseId : vcCaseID) || 0;
      const videoRoom = await joinVideoRoom(targetCaseId, userInfo?.role?.name);

      const checkDependentParticipant = videoRoom.participants.find((participant) => {
        return participant.user.role.name === 'Dependent';
      });

      await startVideoRoom(videoRoom.sid, localTracks, checkDependentParticipant?.identity || '');

      dispatchSuccessEvent('video-room-connected', videoRoom.sid);
      return true;
    } catch (error) {
      setDisableBtn(false);
      if (!(error as { videoConnectFailureHandled?: boolean })?.videoConnectFailureHandled) {
        toast.error('Something went wrong while joining the video room. Please try again later.', {
          className: 'fs-unmask',
        });
      }
      return false;
    }
  };

  const getLocalTracks = async (ctrl: AbortController): Promise<void> => {
    try {
      await getAudioAndVideoTracks(ctrl);
    } catch (error: any) {
      toast.error('Something went wrong while getting local tracks. Please try again later.', {
        className: 'fs-unmask',
      });
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const curCase = await getOneCase(Number(userInfo?.role?.name === UserRoleName.Patient ? p.caseId : vcCaseID));
        setCurrentCase(curCase);
        if (curCase) {
          const activeVideoRoom = await getActiveVideoRoom(
            Number(userInfo?.role?.name === UserRoleName.Patient ? p.caseId : vcCaseID),
            userInfo?.role?.name,
          );

          if (activeVideoRoom) {
            const connectedParticipants = activeVideoRoom.participants
              .filter((participant) => {
                return participant.status === 'connected' && participant.user.role.name !== UserRoleName.Patient;
              })
              .map((participant) => participant.user.id);
            setNonPatientParticipantIds([...nonPatientParticipantIds, ...connectedParticipants]);
            dispatchSuccessEvent('video-room-joined', activeVideoRoom.sid);
          }

          if (localTracks.length === 0) {
            await getLocalTracks(controller);
            await setTimeout(() => setWaitForTracks(true), 2000);
          }
        }
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
      return () => {
        setCurrentCase(undefined);
      };
    })();
    const subscriptionId = subscribe(subscriber);
    window.addEventListener('beforeunload', () => {
      setNonPatientParticipantIds([]);
    });
    return () => {
      unsubscribe(subscriptionId);
      controller.abort();
      window.removeEventListener('beforeunload', () => {
        setNonPatientParticipantIds([]);
      });
    };
  }, []);

  const isJoinDisabled =
    (userInfo?.role?.name === UserRoleName.Patient && !isPatientAbleToJoin) ||
    localTracks.length === 0 ||
    !waitForTracks ||
    disableBtn;
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

  const scheduledText = useMemo(() => {
    if (!currentCase?.video_conference_date) return '';

    return formatDateTime(currentCase.video_conference_date, timeZone);
  }, [currentCase?.video_conference_date]);

  const hintText = useMemo(() => {
    if (userInfo?.role?.name !== UserRoleName.Patient) return '';
    if (currentCase?.type === CaseType.video_call_scheduled || currentCase?.type === CaseType.oral_health_coaching) {
      return 'You can join the video call 10 minutes before your appointment.';
    }
    if (currentCase?.type === CaseType.video_call_instant) {
      return 'You can close this window if you’d prefer not to wait and we’ll send an alert (SMS and/or email) as soon as your dentist arrives.';
    }
    return '';
  }, [currentCase?.type, userInfo?.role?.name]);

  useEffect(() => {
    let timeoutId: number | undefined;

    if (localTracks.length > 0) {
      timeoutId = window.setTimeout(() => {
        setWaitForTracks(true);
      }, 2000);
    } else {
      setWaitForTracks(false);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [localTracks.length]);

  const ControlsBar = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: isMini ? 0.5 : 2,
      }}
    >
      <Box
        sx={{
          height: 56,
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
        }}
      >
        <ToggleAudioButton layout={layout} />
        <ToggleVideoButton layout={layout} />
        <EndCallButton endCurrentCall={endCurrentCall} layout={layout} />
      </Box>
    </Box>
  );

  const JoinButton = (
    <MuiButton
      variant="contained"
      disableElevation
      fullWidth
      className="fs-unmask"
      onClick={async () => {
        const joined = await start();
        if (joined) setIsJoined(true);
      }}
      disabled={isJoinDisabled}
      sx={{
        height: layout === 'full' ? 44 : 30,
        borderRadius: '999px',
        textTransform: 'none',
        fontWeight: 800,
        fontSize: layout === 'full' ? 15 : 12,
        '&:hover': { boxShadow: 'none' },
        '&.Mui-disabled': {
          opacity: 0.6,
          color: 'rgba(255,255,255,0.9)',
        },
        marginTop: layout === 'mini' ? '8px' : 'unset',
      }}
    >
      Join now
    </MuiButton>
  );

  const ProviderHeaderRow =
    (checkPatientStatus('notification') && currentCase?.provider) ||
    (userInfo?.role?.name === UserRoleName.Patient && !checkPatientStatus('notification')) ||
    (currentCase?.provider === null &&
      userInfo?.role?.name === UserRoleName.Patient &&
      checkPatientStatus('notification'));

  const ProviderInfo = (
    <>
      {checkPatientStatus('notification') && currentCase?.provider && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            onClick={() => setProviderProfileCardModal(true)}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <Avatar
              src={getUserAvatar(Number(currentCase?.provider?.id))}
              sx={{ width: 40, height: 40 }}
              imgProps={{
                onError: (event: any) => {
                  event.target.src =
                    'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                  event.target.onError = null;
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 800 }} className="fs-unmask">
              {currentCase?.provider?.full_name}
              {currentCase?.provider?.provider_profile?.credentials ? ', ' : ''}
              {currentCase?.provider?.provider_profile?.credentials || ''}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>will join you shortly</Typography>
          </Box>
        </Box>
      )}

      {currentCase?.provider === null &&
        userInfo?.role?.name === UserRoleName.Patient &&
        checkPatientStatus('notification') && (
          <Box>
            <Typography sx={{ fontWeight: 800 }} className="fs-unmask">
              Your {currentCase?.type === CaseType.oral_health_coaching ? 'oral health coach' : 'dentist'} will join
              shortly.
            </Typography>
          </Box>
        )}

      {userInfo?.role?.name === UserRoleName.Patient && !checkPatientStatus('notification') && (
        <Box>
          <Typography sx={{ fontWeight: 800 }} className="fs-unmask">
            Your {currentCase?.type === CaseType.oral_health_coaching ? 'oral health coach' : 'dentist'} will join
            shortly.
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <>
      {userInfo?.role.name === UserRoleName.Patient && (
        <Sidenav isVisibleOnMobile isProfileCard={false}>
          {false && (
            <div className={styles.sidenavContainer}>
              <div className={styles.sidenavToggleText}>Chat with Care Advisor</div>
              <div className={styles.sidenavToggle}>
                <BiMessageAltDetail />
              </div>
            </div>
          )}
        </Sidenav>
      )}
      {userInfo?.role.name === UserRoleName.Patient ? (
        <div
          className={styles.videoBody}
          style={{
            paddingTop: isVisible ? '20px' : '',
            paddingBottom: isVisible ? '20px' : '',
            backgroundColor: isVisible ? 'unset' : '',
            alignItems: isVisible ? 'flex-start' : '',
          }}
        >
          <div className={styles.leftSection}>
            <div
              className={`${styles.videoContainer} ${
                userInfo?.role?.name === UserRoleName.Patient ? styles.videoContainerPatient : ''
              }`}
              style={{
                width: userInfo?.role?.name !== UserRoleName.Patient ? 'max-content' : isMobile ? '100%' : '400px',
                minWidth: userInfo?.role?.name !== UserRoleName.Patient ? '100%' : isMobile ? '0' : 'inherit',
              }}
            >
              {checkPatientStatus('notification') && currentCase?.provider && (
                <div className={styles.providerInfo}>
                  <div onClick={() => setProviderProfileCardModal(true)}>
                    <img
                      alt="Avatar"
                      onError={(event: any) => {
                        event.target.src =
                          'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                        event.target.onError = null;
                      }}
                      className={`fs.exclude ${styles.profileImage}`}
                      src={getUserAvatar(Number(currentCase?.provider?.id))}
                    />
                  </div>
                  <div className={styles.providerContent}>
                    <div className={`fs.exclude ${styles.providerName}`}>
                      {currentCase?.provider?.full_name}
                      {currentCase?.provider.provider_profile?.credentials ? ', ' : ''}
                      {currentCase?.provider.provider_profile?.credentials || ''}
                    </div>
                    <div className={styles.providerNotification}>will join you shortly</div>
                  </div>
                </div>
              )}
              {currentCase?.provider === null &&
                userInfo?.role.name === UserRoleName.Patient &&
                checkPatientStatus('notification') && (
                  <div className={styles.providerInfo}>
                    <div className={styles.providerContent}>
                      <div className={styles.providerNotification}>
                        Your {currentCase?.type === CaseType.oral_health_coaching ? 'oral health coach' : 'dentist'}{' '}
                        will join shortly.
                      </div>
                    </div>
                  </div>
                )}
              {userInfo?.role.name === UserRoleName.Patient && !checkPatientStatus('notification') && (
                <div className={styles.providerInfo}>
                  <div className={styles.providerContent}>
                    <div className={styles.providerNotification}>
                      Your {currentCase?.type === CaseType.oral_health_coaching ? 'oral health coach' : 'dentist'} will
                      join shortly.
                    </div>
                  </div>
                </div>
              )}
              <div
                className={styles.videoTrackContainer}
                style={{
                  width: isMobile ? 'min(90%, 280px)' : '',
                  height: isMobile ? 'min(42dvh, 320px)' : '',
                }}
              >
                <LocalVideoPreview />
                <div
                  className={`${styles.videoIconsContainer} ${
                    userInfo?.role.name === UserRoleName.Patient ? styles.videoIconsContainerPatient : ''
                  }`}
                >
                  <ToggleAudioButton />
                  <ToggleVideoButton />
                  {isSupported && <VirtualBackgroundButton />}
                  <CameraSwitch isWaitingRoom />
                </div>
              </div>
              {userInfo?.role.name === UserRoleName.Patient && (
                <div className={`fs-unmask ${styles.notificationMessage}`}>
                  {currentCase?.type === CaseType.video_call_scheduled ||
                  currentCase?.type === CaseType.oral_health_coaching
                    ? 'You can join the video call 10 minutes before your appointment.'
                    : currentCase?.type === CaseType.video_call_instant
                    ? 'You can close this window if you’d prefer not to wait and we’ll send an alert (SMS and/or email) as soon as your dentist arrives.'
                    : ''}
                </div>
              )}
              <Button
                className={`fs-unmask ${styles.joinButton}`}
                type="button"
                onClick={async () => {
                  const joined = await start();
                  if (joined) setIsJoined(true);
                }}
                disabled={
                  (userInfo?.role.name === UserRoleName.Patient && !isPatientAbleToJoin) ||
                  localTracks.length === 0 ||
                  !waitForTracks ||
                  disableBtn
                }
              >
                Join Now
              </Button>
            </div>
          </div>
          {userInfo?.role.name === UserRoleName.Patient && (
            <div className={styles.rightImage}>
              <div
                className={`${styles.cardImage} ${styles.onlineLearning}`}
                style={{ backgroundImage: `url(${rightImage})` }}
              />
            </div>
          )}
        </div>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'transparent',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              p: isMini ? 2 : 3,
              gap: isMini ? 1.5 : 2,
            }}
          >
            {ProviderHeaderRow && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                {ProviderInfo}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} />
              </Box>
            )}

            {!isMini ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'grid',
                  gridTemplateColumns: '1.35fr 0.65fr',
                  gap: 3,
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      borderRadius: 3,
                      overflow: 'hidden',
                      bgcolor: '#000',
                      position: 'relative',
                    }}
                  >
                    <LocalVideoPreview />
                  </Box>

                  {ControlsBar}

                  {!!hintText && (
                    <Typography sx={{ mt: 2, color: 'text.secondary' }} className="fs-unmask">
                      {hintText}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    className="fs-unmask"
                    sx={{
                      fontWeight: 900,
                      fontSize: 38,
                      lineHeight: 1.05,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {getFriendlyName(currentCase?.type || '')}
                  </Typography>

                  <Typography className="fs-unmask" sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>
                    Scheduled for
                  </Typography>

                  <Typography className="fs-unmask" sx={{ mt: 0.5, fontWeight: 700, fontSize: 15 }}>
                    {scheduledText}
                  </Typography>

                  <Box sx={{ mt: 3, width: '100%' }}>{JoinButton}</Box>
                </Box>
              </Box>
            ) : !isMobile ? (
              /* Desktop mini panel — original compact layout (fit-content height, no aspect-ratio forcing) */
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography className="fs-unmask" sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.1 }}>
                    {getFriendlyName(currentCase?.type || '')}
                  </Typography>

                  <Typography className="fs-unmask" sx={{ mt: 0.75, color: 'text.secondary', fontSize: 14 }}>
                    Scheduled for
                  </Typography>

                  <Typography className="fs-unmask" sx={{ mt: 0.25, fontWeight: 700, fontSize: 14 }}>
                    {scheduledText}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#000',
                    height: 'fit-content',
                  }}
                >
                  <LocalVideoPreview />
                </Box>

                <Box>{JoinButton}</Box>

                {ControlsBar}

                {!!hintText && (
                  <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: 12 }} className="fs-unmask">
                    {hintText}
                  </Typography>
                )}
              </Box>
            ) : (
              /* Mobile — portrait 9:16 video with controls overlaid, height flex-driven */
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  px: 2,
                  pt: 1.5,
                  pb: 1,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 1, flexShrink: 0 }}>
                  <Typography className="fs-unmask" sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.1 }}>
                    {getFriendlyName(currentCase?.type || '')}
                  </Typography>

                  <Typography className="fs-unmask" sx={{ mt: 0.75, color: 'text.secondary', fontSize: 14 }}>
                    Scheduled for
                  </Typography>

                  <Typography className="fs-unmask" sx={{ mt: 0.25, fontWeight: 700, fontSize: 14 }}>
                    {scheduledText}
                  </Typography>
                </Box>

                {/* 9:16 portrait frame — height driven by flex, width auto from ratio */}
                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    alignSelf: 'center',
                    aspectRatio: '12 / 16',
                    maxWidth: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#000',
                  }}
                >
                  <Box sx={{ position: 'absolute', inset: 0 }}>
                    <LocalVideoPreview />
                  </Box>

                  {/* Mute / camera / hang-up overlay.
                      transform: translateZ(0) promotes this to a GPU compositing layer so it
                      renders above the <video> element on iOS/mobile (which always composites
                      on top of regular stacking-context siblings). */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 10,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      zIndex: 2,
                      transform: 'translateZ(0)',
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 999,
                        bgcolor: 'rgba(0,0,0,0.28)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 0.5,
                        py: 0.25,
                      }}
                    >
                      <ToggleAudioButton layout={layout} />
                      <ToggleVideoButton layout={layout} />
                      <EndCallButton endCurrentCall={endCurrentCall} layout={layout} />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mt: 1.5, flexShrink: 0 }}>{JoinButton}</Box>

                {!!hintText && (
                  <Typography
                    sx={{ mt: 1, color: 'text.secondary', fontSize: 12, flexShrink: 0 }}
                    className="fs-unmask"
                  >
                    {hintText}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
      {providerProfileCardModal && (
        <ProviderProfileCardModal
          isOpen={providerProfileCardModal}
          toggleModal={setProviderProfileCardModal}
          providerIdentity={currentCase?.provider || null}
          patientCurrentStateValue={currentCase?.patient_current_state}
          caseId={currentCase?.id}
        />
      )}
    </>
  );
};

export default React.memo(WaitingRoom);
